import { DrawerContent } from "./drawerContent.js";
import { Helper } from "../app/core/util/helper.js";

export class Drawer {
    private _hasValue = Helper.hasValue;
    private _element: JQuery = null;
    private _toggleElement: JQuery = null;
    private _contentWrapper: JQuery = null;
    private _contentElements: Array<DrawerContent> = [];
    private _direction: DrawerDirection = null;
    private _style: DrawerStyle = DrawerStyle.SlideContent;
    private _isOpen = false;
    private _autoOpen = false;
    private _saveState = false;
    private _onAnimationEnd: Function = null;
    private _customWidth: string | number = null;
    private _toggleCallbacks: Array<(isOpen: boolean) => void> = [];
    private _resizeCallbacks: Array<() => void> = [];
    private _hiddenContentContainer: JQuery = null;
    private _toggleButton: JQuery = null;

    constructor(element: JQuery, direction: DrawerDirection, hiddenContentContainer: JQuery, style?: DrawerStyle, autoOpen?: boolean, saveState?: boolean) {
        this._element = element;
        this._direction = direction;
        this._hiddenContentContainer = hiddenContentContainer;

        this._element.addClass("drawer").addClass("drawer--closed").addClass("drawer--inactive");
        this._autoOpen = autoOpen === true;
        this._saveState = saveState === true && direction === DrawerDirection.FromLeft;
        this._toggleElement = $("<div>").addClass("drawer__toggle");
        this._toggleButton = $('<i class="fal fa-2x fa-fw" aria-hidden="true">');
        if (direction === DrawerDirection.FromLeft) {
            this._element.addClass("drawer--left");
            this._toggleButton.addClass("fa-angle-double-left");
        } else if (direction === DrawerDirection.FromRight) {
            this._element.addClass("drawer--right");
            this._toggleButton.addClass("fa-angle-double-right");
        }
        if (this._hasValue(style)) {
            this._style = style;
        }
        if (this._style === DrawerStyle.SlideOver) {
            this._element.addClass("drawer--slide-over");
        }

        this._toggleElement.append(this._toggleButton);
        this._toggleElement.on("click", () => {
            this._toggle();
        })

        this._contentWrapper = $("<div>").addClass("drawer__content-wrapper");

        this._element.append(this._toggleElement);
        this._element.append(this._contentWrapper);

        this._contentWrapper.on("animationend transitionend", (je: JQueryEventObject) => {
            if (je.target === this._contentWrapper[0]) {
                if (this._hasValue(this._onAnimationEnd)) {
                    this._onAnimationEnd();
                }
                this._toggleCallbacks.forEach(c => {
                    c(this._isOpen);
                });
            }
        });

        this._element.on("animationend transitionend", (je: JQueryEventObject) => {
            if (je.target === this._element[0]) {
                this._resizeCallbacks.forEach(c => c());
            }
        });
    }


    private _toggle = () => {
        this.updateStorage();
        if (this._isOpen) {
            this.close();
        } else {
            this.open();
        }

    }

    private updateStorage() {
        if (this._saveState)
            Helper.saveDataToStorage("DrawerLeft", !this._isOpen, false);
    }

    open = (afterAnimation?: Function) => {
        this._onAnimationEnd = afterAnimation;
        this._element.removeClass("drawer--closed");
        this._toggleButton.removeClass("fa-angle-double-left");
        this._toggleButton.addClass("fa-angle-double-right");
        this._isOpen = true;
        this.updateWidth();
    }

    close = (afterAnimation?: Function) => {
        this._onAnimationEnd = afterAnimation;
        this._element.width("");
        this._element.addClass("drawer--closed");
        this._toggleButton.addClass("fa-angle-double-left");
        this._toggleButton.removeClass("fa-angle-double-right");
        this._isOpen = false;
        if (this._contentWrapper.get(0).scrollHeight > this._contentWrapper.innerHeight()) {
            const width = 50 + this._contentWrapper.get(0).offsetWidth - this._contentWrapper.get(0).clientWidth;
            this._element.width(`${width}px`);
        }
    }

    setStyle = (style: DrawerStyle) => {
        if (this._style !== style) {
            this._style = style;
            if (this._style === DrawerStyle.SlideOver) {
                this._element.addClass("drawer--slide-over");
            } else {
                this._element.removeClass("drawer--slide-over");
            }
        }
    }

    getStyle = () => {
        return this._style;
    }

    createContentElement = () => {
        const content = new DrawerContent(this, this._hiddenContentContainer);
        this._contentWrapper.append(content.getElement());
        this._contentElements.push(content);
        this.enable();
        const savedState = this._saveState ? Helper.loadDataFromStorage("DrawerLeft") : null;
        if ((savedState === true || (savedState == null && this._autoOpen)) && !Helper.useMobileGui()) {
            this.open();
        }
        return content;
    }

    removeLastContent = () => {
        this._contentElements.pop();
        if (this._contentElements.length === 0) {
            this.disable();
        }
    }

    removeContent = (content: DrawerContent) => {
        for (let i = 0; i < this._contentElements.length; i++) {
            if (this._contentElements[i] === content) {
                this._contentElements.splice(i, 1);
                if (this._contentElements.length === 0) {
                    this.disable();
                }
                break;
            }
        }
    }

    enable = () => {
        this._element.removeClass("drawer--inactive");
    }

    disable = () => {
        this.close();
        this._element.addClass("drawer--inactive");
    }

    elements = {
        getElement: () => {
            return this._element;
        },
        getToggleElement: () => {
            return this._toggleElement;
        },
        getContentWrapper: () => {
            return this._contentWrapper;
        }
    }

    updateWidth = () => {
        const width = this._contentElements.length > 0 ?
            this._contentElements[this._contentElements.length - 1].getCustomWidth()
            : null;

        //need "" because jQuery does not reset when using "null"
        let toSet: number | string = "";
        if (this._hasValue(width) && width !== 0) {
            if (typeof width === "number") {
                toSet = width.toString() + "px";
            } else {
                toSet = width;
            }
        }
        this._customWidth = width;
        if (this.isOpen()) {
            this._contentWrapper.width(toSet);
            if (this._style === DrawerStyle.SlideContent) {
                this._element.width(toSet);
            } else {
                this._element.width("");
            }
        }
    }

    isOpen = () => {
        return this._isOpen;
    }

    isDisabled = () => {
        return this._element.hasClass("drawer--inactive");
    }

    onToggle = (callback: (isOpen: boolean) => void) => {
        this._toggleCallbacks.push(callback);
    }

    onResize = (callback: () => void) => {
        this._resizeCallbacks.push(callback);
    }

    deregisterToggle = (callback: (isOpen: boolean) => void) => {
        let idx = -1;

        for (var i = 0; i < this._toggleCallbacks.length; i++) {
            if (this._toggleCallbacks[i] === callback) {
                idx = i;
                break;
            }
        }

        if (idx > -1) {
            this._toggleCallbacks.splice(idx, 1);
        }
    }

    deregisterResize = (callback: () => void) => {
        let idx = -1;

        for (var i = 0; i < this._resizeCallbacks.length; i++) {
            if (this._resizeCallbacks[i] === callback) {
                idx = i;
                break;
            }
        }

        if (idx > -1) {
            this._resizeCallbacks.splice(idx, 1);
        }
    }

    disableAnimation = () => {
        this._element.addClass("drawer--no-animation");
    }

    enableAnimation = () => {
        this._element.removeClass("drawer--no-animation");
    }

    setSaveState = (saveState: boolean) => {
        this._saveState = saveState;
    }
}

export enum DrawerDirection {
    FromLeft, FromRight
}

export enum DrawerStyle {
    SlideContent, SlideOver
}